import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    TextField,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Collapse,
    IconButton,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { ChecklistCategory } from '@types';

interface ChecklistTreeProps {
    items: ChecklistCategory[];
    selected: number[];
    disabled?: boolean;
    onUpdateCategories: (categoryIds: number[]) => void;
}

const ChecklistTree: React.FC<ChecklistTreeProps> = ({
    items,
    selected,
    disabled = false,
    onUpdateCategories,
}) => {
    const [filterText, setFilterText] = useState('');
    const [selectedIds, setSelectedIds] = useState<number[]>(selected);
    const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

    useEffect(() => {
        setSelectedIds(selected);
    }, [selected]);

    const filterNode = (node: ChecklistCategory, searchText: string): boolean => {
        if (!searchText) return true;

        const matchesSearch = node.name.toLowerCase().includes(searchText.toLowerCase());
        const childrenMatch = node.children?.some(child => filterNode(child, searchText));

        return matchesSearch || childrenMatch;
    };

    const handleToggleExpand = (nodeId: number) => {
        const newExpanded = new Set(expandedNodes);
        if (newExpanded.has(nodeId)) {
            newExpanded.delete(nodeId);
        } else {
            newExpanded.add(nodeId);
        }
        setExpandedNodes(newExpanded);
    };

    const handleCheckChange = (nodeId: number, checked: boolean, node: ChecklistCategory) => {
        let newSelected = [...selectedIds];

        // Handle current node
        if (checked) {
            if (!newSelected.includes(nodeId)) {
                newSelected.push(nodeId);
            }
        } else {
            newSelected = newSelected.filter(id => id !== nodeId);
        }

        // Handle children recursively
        const updateChildren = (children: ChecklistCategory[], shouldCheck: boolean) => {
            children.forEach(child => {
                if (shouldCheck) {
                    if (!newSelected.includes(child.id)) {
                        newSelected.push(child.id);
                    }
                } else {
                    newSelected = newSelected.filter(id => id !== child.id);
                }
                if (child.children && child.children.length > 0) {
                    updateChildren(child.children, shouldCheck);
                }
            });
        };

        if (node.children && node.children.length > 0) {
            updateChildren(node.children, checked);
        }

        setSelectedIds(newSelected);
        onUpdateCategories(newSelected);
    };

    const renderTreeNode = (node: ChecklistCategory, level: number = 0): JSX.Element | null => {
        if (!filterNode(node, filterText)) {
            return null;
        }

        const isChecked = selectedIds.includes(node.id);
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedNodes.has(node.id);

        return (
            <React.Fragment key={node.id}>
                <ListItem
                    disablePadding
                    sx={{
                        pl: level * 3,
                        ml: level > 0 ? 2 : 0,
                    }}
                >
                    <ListItemButton
                        dense
                        onClick={() => hasChildren && handleToggleExpand(node.id)}
                        sx={{
                            '&:hover': {
                                backgroundColor: 'rgba(248, 177, 51, 0.08)',
                            },
                        }}
                    >
                        {hasChildren && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleExpand(node.id);
                                }}
                                sx={{ mr: 1, p: 0.5 }}
                            >
                                {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                            </IconButton>
                        )}
                        {!hasChildren && <Box sx={{ width: 32 }} />}

                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox
                                edge="start"
                                checked={isChecked}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    handleCheckChange(node.id, e.target.checked, node);
                                }}
                                disabled={disabled}
                                size="small"
                                sx={{
                                    color: '#F8B133',
                                    '&.Mui-checked': {
                                        color: '#F8B133',
                                    },
                                }}
                            />
                        </ListItemIcon>

                        <ListItemText
                            primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{node.name}</span>
                                    {node.required && (
                                        <span style={{ color: '#f44336', fontSize: '12px', fontWeight: 'bold' }}>*</span>
                                    )}
                                </Box>
                            }
                            secondary={node.description}
                            primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: isChecked ? 600 : 400,
                            }}
                            secondaryTypographyProps={{
                                fontSize: '0.75rem',
                            }}
                        />
                    </ListItemButton>
                </ListItem>

                {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {node.children.map(child => renderTreeNode(child, level + 1))}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        );
    };

    return (
        <Card variant="outlined" sx={{ p: 2 }}>
            <TextField
                fullWidth
                size="small"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="Search..."
                variant="outlined"
                sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                            borderColor: '#F8B133',
                        },
                    },
                }}
            />

            <Box
                sx={{
                    maxHeight: 400,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#F8B133',
                        borderRadius: '4px',
                    },
                }}
            >
                <List component="nav" disablePadding>
                    {items.map(node => renderTreeNode(node, 0))}
                </List>
            </Box>
        </Card>
    );
};

export default ChecklistTree;
